import React, { memo, useMemo } from "react"
import { css } from "@emotion/react"
import { Link, useHistory } from "react-router-dom"
import {
	Box,
	PartPriceEcomm,
	TrackingEvent,
	PartClearancePriceEcomm,
	ButtonEcomm,
	CardProps,
	Paragraph,
	calculateEcommDiscount,
	PartImage,
	EventTracker,
	Icon,
} from "@ncs/web-legos"
import {
	CustomerPart,
	isNonParentCustomerPart,
	NonParentCustomerPart,
	useOnlinePartDetails,
	OnlinePartDetails,
} from "@ncs/ncs-api"
import { useShopContext } from "~/contexts"
import ComingSoon from "/parts/coming-soon.svg"
import { useAnalytics } from "~/hooks/useAnalytics"

type WidthModeType = "fixed" | "fluid"
interface PartCardProps extends CardProps {
	part: CustomerPart
	onAddToCart: (partId: string) => void
	widthMode?: WidthModeType
}

export const PartCardEcomm: React.FC<PartCardProps> = memo(
	({ part, onAddToCart, widthMode, ...rest }) => {
		const history = useHistory()
		const [, shopDispatch] = useShopContext()
		const [partDetails] = useOnlinePartDetails(part.isParent ? part.id : undefined)
		const { trackEvent } = useAnalytics()

		const handleAddToCart = (partToAdd: NonParentCustomerPart) => {
			shopDispatch({
				type: "add part to cart",
				payload: { part: partToAdd },
			})

			// Fire the passed in handler to open to the added to cart modal.
			onAddToCart(partToAdd.id)
			trackEvent(TrackingEvent.FLOATING_CART, {
				id: partToAdd.id,
				name: partToAdd.description,
			})
		}

		const goToProduct = (id: string) => {
			history.push(`/shop/parts/${id}`)
			trackEvent(TrackingEvent.GO_TO_PRODUCT, { id, name: part.description })
		}

		const displayNetPrice = (cardPart: CustomerPart): number | null => {
			return cardPart.isParent ? cardPart.childNetPrice : cardPart.netPrice
		}

		const partCardImg = (
			cardPart: CustomerPart,
			cardPartDetails: OnlinePartDetails | undefined
		): string => {
			let img = ComingSoon
			if (cardPart.isParent && cardPart.childImage) {
				img = cardPart.childImage ?? ComingSoon
			} else if (cardPartDetails?.children?.some((p) => p.imageUrl)) {
				img = cardPartDetails?.children?.find((p) => p.imageUrl)?.imageUrl
			} else {
				img = cardPart.imageUrl ?? ComingSoon
			}
			return img
		}

		const isClearanceItem = useMemo(() => {
			if (part.isParent && partDetails && partDetails.children) {
				return partDetails.children.some((p) => p.clearanceItem)
			}
			return part.clearanceItem
		}, [part, partDetails])

		const [clearanceDiscount, clearancePrice] = useMemo(() => {
			if (part.isParent && partDetails && partDetails.children) {
				let discount = 0
				let price = 0

				for (const p of partDetails.children) {
					if (
						(calculateEcommDiscount(p.clearanceItemDiscount, p.price) ?? 0) > discount
					) {
						discount = calculateEcommDiscount(p.clearanceItemDiscount, p.price) ?? 0
						price = p.price
					}
				}

				return [discount, price]
			}
			return [calculateEcommDiscount(part.clearanceItemDiscount, part.price), part.price]
		}, [part, partDetails])

		return (
			<div css={() => partCard(widthMode ?? "fixed")}>
				<>
					{isClearanceItem && (
						<Box display="flex" flexDirection="row" css={clearanceTag}>
							<Icon icon="tag" />
							<span>
								{part?.isParent && "Up To"} {clearanceDiscount}% Off
							</span>
						</Box>
					)}
					<EventTracker
						event={TrackingEvent.CARD}
						data={{ id: part.id, name: part.description }}
						trackEvent={trackEvent}
					>
						<Link to={`/shop/parts/${part.id}`}>
							<PartImage
								src={partCardImg(part, partDetails)}
								css={partImageContainerStyle}
								imageCss={stylePartImage}
								alt={part.title}
								className="part-card-image"
							/>
						</Link>
					</EventTracker>

					<EventTracker
						event={TrackingEvent.CARD}
						data={{ id: part.id, name: part.description }}
						trackEvent={trackEvent}
					>
						<Paragraph
							mt={1.5}
							mb={0.5}
							color="secondary"
							fontSize={0.8}
							customColor="#003264"
							bold
							textTransform="uppercase"
						>
							{part?.brandName ? part?.brandName : "n/a"}
						</Paragraph>
						<Link to={`/shop/parts/${part.id}`} css={headingLinkStyle}>
							<Paragraph css={productTitle} className="product-title">
								{part.title}
							</Paragraph>
						</Link>
					</EventTracker>

					{isClearanceItem ?
						<PartClearancePriceEcomm
							netPrice={clearancePrice}
							price={part.clearanceItemDiscount}
							isParent={part?.isParent}
							clearanceDiscount={clearanceDiscount}
							showClearanceLegend
						/>
					:	<>
							<Paragraph mb={1.5} mt={0.4} fontSize={0.8} bold>
								{isNonParentCustomerPart(part) ?
									`#${part.onlinePartNumber}`
								:	`${part?.variantCount} Options Available`}
							</Paragraph>
							<PartPriceEcomm
								netPrice={displayNetPrice(part) ?? 0}
								price={part?.price}
								isParent={part?.isParent}
							/>
						</>
					}
				</>
				{isNonParentCustomerPart(part) ?
					<ButtonEcomm
						variant="primary-cta"
						onClick={() => handleAddToCart(part)}
						fillContainer
						containerProps={{
							mt: 2,
						}}
					>
						Add To Cart
					</ButtonEcomm>
				:	<ButtonEcomm
						variant="secondary-cta"
						onClick={() => goToProduct(part.id)}
						fillContainer
						containerProps={{
							mt: 2,
						}}
					>
						See Options
					</ButtonEcomm>
				}
			</div>
		)
	}
)

const partCard = (widthMode: WidthModeType = "fixed") => css`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	transition: box-shadow 0.3s ease-in-out;
	width: ${widthMode === "fixed" ? "220px" : "100%"};
	max-height: 500px;
	height: 500px;
	min-height: 500px;
	position: relative;
	z-index: 0;

	a:hover,
	a:focus {
		text-decoration: none;
	}

	&:hover {
		cursor: pointer;
	}

	&:hover button.css-dvbig9-baseButtonStyle-buttonWidthStyle-variantStyle {
		background-color: #00254a;
	}

	&:hover button.css-upcgpj-baseButtonStyle-buttonWidthStyle-variantStyle {
		border: 2px solid #00254a;
		color: #00254a;
	}

	&:hover div.part-card-image {
		box-shadow: 0 0 0.375rem 0 #0b75e1;
	}

	&:hover p.product-title {
		color: #0b75e1;
	}
`

const clearanceTag = css`
	min-width: 102px;
	background-color: #dc2626;
	display: flex;
	align-items: center;
	padding: 0.3rem 12px;
	position: absolute;
	z-index: 1;
	border-radius: 4px 0px;

	svg {
		margin-top: 5px;
		margin-right: 8px;
	}

	> p,
	> span {
		color: #ffffff;
		font-size: 0.875rem;
		font-weight: 700;
	}
`

const headingLinkStyle = css`
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`

const partImageContainerStyle = css`
	width: 100%;
	padding: 1.25rem;
	border-radius: 0.25rem;
	box-shadow: 0 0 0.375rem 0 #d1d5db;
	background-color: #fff;
	display: block;
`

const stylePartImage = css`
	max-height: 160px;
	height: 160px;
	min-height: 160px;
	width: 100%;
	object-fit: contain;
	object-position: center;
`

const productTitle = css`
	font-size: 1.25rem;
	font-weight: 500;
	line-height: normal;
	overflow-wrap: anywhere;
	overflow: hidden;
	text-overflow: ellipsis;
	display: block;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
	line-height: 1.1;
	max-height: 4.5em;
	overflow: hidden;
`
